import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, HeartPulse, ListTree, Plus, Table2, Target } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  useToast,
  type BadgeTone,
  type SelectOption,
  type TabItem,
} from '../../ui';
import { useGoals } from '../../features/goals/useGoals';
import {
  GOAL_STATUSES,
  collectPeriods,
  collectTypes,
  filterGoals,
} from '../../features/goals/goalModel';
import { optionLabel, GOAL_TYPES, PLANNING_PERIODS } from '../../features/goals/goalConfig';
import type { GoalFilter, GoalStatus, GoalSummary } from '../../features/goals/types';
import { GoalListView } from './GoalListView';
import { GoalTreeView } from './GoalTreeView';
import { GoalModal } from './GoalModal';

type ViewMode = 'list' | 'tree';

const VIEW_TABS: TabItem[] = [
  { id: 'list', label: 'List', icon: <Table2 className="h-4 w-4" /> },
  { id: 'tree', label: 'Hierarchy', icon: <ListTree className="h-4 w-4" /> },
];

const EMPTY_FILTER: GoalFilter = { goalType: '', planningPeriod: '', status: '' };

/** Health-board grouping order (risk first) + badge tones. */
const HEALTH_GROUPS: { status: GoalStatus; tone: BadgeTone }[] = [
  { status: 'OFF_TRACK', tone: 'danger' },
  { status: 'AT_RISK', tone: 'warning' },
  { status: 'ON_TRACK', tone: 'success' },
  { status: 'NOT_STARTED', tone: 'neutral' },
  { status: 'COMPLETED', tone: 'info' },
];

const STATUS_LABEL: Record<GoalStatus, string> = Object.fromEntries(
  GOAL_STATUSES.map((s) => [s.value, s.label]),
) as Record<GoalStatus, string>;

/**
 * Goals page — a first-class hub for team goals. Lists goals (filterable by
 * type / period / status) with a List ⇄ Hierarchy view toggle, and supports
 * creating a goal via a modal. Reads exclusively through the useGoals hook.
 */
export function GoalsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { goals, loading, error, create } = useGoals();

  const [view, setView] = useState<ViewMode>('list');
  const [filter, setFilter] = useState<GoalFilter>(EMPTY_FILTER);
  const [modalOpen, setModalOpen] = useState(false);

  const typeOptions = useMemo<SelectOption[]>(
    () =>
      collectTypes(goals).map((t) => ({
        value: t,
        label: optionLabel(GOAL_TYPES, t),
      })),
    [goals],
  );
  const periodOptions = useMemo<SelectOption[]>(
    () =>
      collectPeriods(goals).map((p) => ({
        value: p,
        label: optionLabel(PLANNING_PERIODS, p),
      })),
    [goals],
  );
  const statusOptions = useMemo<SelectOption[]>(
    () => GOAL_STATUSES.map((s) => ({ value: s.value, label: s.label })),
    [],
  );

  const filtered = useMemo(() => filterGoals(goals, filter), [goals, filter]);
  const filtersActive = !!(
    filter.goalType ||
    filter.planningPeriod ||
    filter.status
  );

  // Additive quick-views driven by the URL (sidebar deep-links): a framework
  // scope (?framework=BHAG) and a status-grouped Goal Health board
  // (?view=health). Both filter the EXISTING summaries client-side — no new
  // route, no new data.
  const framework = searchParams.get('framework') ?? '';
  const healthView = searchParams.get('view') === 'health';
  const scoped = useMemo(
    () =>
      framework ? filtered.filter((g) => g.goalFramework === framework) : filtered,
    [filtered, framework],
  );
  const healthGroups = useMemo(
    () =>
      HEALTH_GROUPS.map((g) => ({
        ...g,
        goals: scoped.filter((x) => x.status === g.status),
      })).filter((g) => g.goals.length > 0),
    [scoped],
  );

  const openGoal = (g: GoalSummary) =>
    navigate(`/goals/${encodeURIComponent(g.id)}`);

  const onCreate = async (draft: Parameters<typeof create>[0]) => {
    const saved = await create(draft);
    toast({ tone: 'success', title: 'Goal created', description: saved.title });
    return saved;
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      <Tabs items={VIEW_TABS} value={view} onChange={(id) => setView(id as ViewMode)} />
      <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
        New Goal
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader
        icon={<Target className="h-5 w-5" />}
        title="Goals"
        description="Track team goals and trace them to engineering work."
        actions={headerActions}
      />

      {/* Active quick-view banner (framework scope / health board) */}
      {(framework || healthView) && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {healthView && (
            <Badge tone="accent" dot>
              <HeartPulse className="h-3 w-3" aria-hidden="true" /> Goal Health board
            </Badge>
          )}
          {framework && <Badge tone="info">Framework: {framework}</Badge>}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/goals')}
          >
            Clear view
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Select
            label="Type"
            placeholder="All types"
            value={filter.goalType}
            options={typeOptions}
            onChange={(e) => setFilter((f) => ({ ...f, goalType: e.target.value }))}
          />
        </div>
        <div className="w-44">
          <Select
            label="Period"
            placeholder="All periods"
            value={filter.planningPeriod}
            options={periodOptions}
            onChange={(e) =>
              setFilter((f) => ({ ...f, planningPeriod: e.target.value }))
            }
          />
        </div>
        <div className="w-44">
          <Select
            label="Status"
            placeholder="All statuses"
            value={filter.status}
            options={statusOptions}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {scoped.length} of {goals.length} {goals.length === 1 ? 'goal' : 'goals'}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading goals" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load goals"
          description={error}
        />
      ) : goals.length === 0 ? (
        <EmptyState
          icon={<Target className="h-6 w-6" />}
          title="No goals yet"
          description="Create your first goal to start tracing engineering work to outcomes."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              New Goal
            </Button>
          }
        />
      ) : scoped.length === 0 ? (
        <EmptyState
          icon={<Target className="h-6 w-6" />}
          title="No goals match this view"
          description={
            framework
              ? `No ${framework} goals with the current filters.`
              : 'No goals match the current filters.'
          }
        />
      ) : healthView ? (
        <div className="space-y-6">
          {healthGroups.map((group) => (
            <div key={group.status}>
              <div className="mb-2 flex items-center gap-2">
                <Badge tone={group.tone} dot>
                  {STATUS_LABEL[group.status]}
                </Badge>
                <span className="text-xs text-zinc-500">
                  {group.goals.length} {group.goals.length === 1 ? 'goal' : 'goals'}
                </span>
              </div>
              <GoalListView goals={group.goals} onSelect={openGoal} />
            </div>
          ))}
        </div>
      ) : view === 'list' ? (
        <GoalListView goals={scoped} onSelect={openGoal} />
      ) : (
        <GoalTreeView goals={scoped} onSelect={openGoal} />
      )}

      <GoalModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        goals={goals}
        onCreate={onCreate}
      />
    </div>
  );
}

export default GoalsPage;
